/* LAV TDMS — composite UI helpers, faithfully ported from the prototype's
   app/ui.jsx (window.APP): completion bars, discipline tags, SLA pills,
   segmented control, and the cumulative S-curve chart. */
import { type CSSProperties, type ReactNode, useEffect, useRef, useState } from 'react'
import { Icon } from '@/ds/icons'
import { sla } from '@/lib/format'

export function CompletionBar({ afd = 0, ifa = 0, ifr = 0, planned = 0, height = 8 }: { afd?: number; ifa?: number; ifr?: number; planned?: number; height?: number }) {
  const total = afd + ifa + ifr + planned || 1
  const seg = (n: number, color: string) =>
    n > 0 ? <span key={color} style={{ width: `${(n / total) * 100}%`, background: `var(${color})` }} /> : null
  return (
    <span style={{ display: 'flex', width: '100%', height, borderRadius: 2, overflow: 'hidden', background: 'var(--surface-sunken)' }}>
      {seg(afd, '--green-500')}
      {seg(ifa, '--blue-500')}
      {seg(ifr, '--amber-400')}
      {seg(planned, '--slate-200')}
    </span>
  )
}

export function DiscTag({ code, name }: { code: string; name?: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: 'var(--text-muted)', background: 'var(--surface-sunken)', border: '1px solid var(--border-default)', borderRadius: 'var(--radius-sm)', padding: '1px 5px' }}>{code}</span>
      {name && <span style={{ font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>{name}</span>}
    </span>
  )
}

export function Crit({ on }: { on?: boolean }) {
  if (!on) return null
  return (
    <span title="Критичный документ" style={{ display: 'inline-grid', placeItems: 'center', color: 'var(--danger-ink)' }}>
      <Icon name="flag" size={13} />
    </span>
  )
}

export function SlaPill({ due, done = false }: { due?: string | null; done?: boolean }) {
  if (done) {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--success-ink)' }}>
        <Icon name="check" size={12} />выполнено
      </span>
    )
  }
  const s = sla(due)
  if (!s) return <span style={{ fontFamily: 'var(--font-mono)', fontSize: 11, color: 'var(--text-subtle)' }}>—</span>
  const map = {
    overdue: { ink: '--danger-ink', bg: '--danger-bg', line: '--danger-line' },
    today: { ink: '--danger-ink', bg: '--danger-bg', line: '--danger-line' },
    soon: { ink: '--warning-ink', bg: '--warning-bg', line: '--warning-line' },
    ok: { ink: '--text-muted', bg: '--surface-sunken', line: '--border-subtle' },
  }[s.tone]
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontFamily: 'var(--font-mono)', fontSize: 11, fontWeight: 600, color: `var(${map.ink})`, background: `var(${map.bg})`, border: `1px solid var(${map.line})`, borderRadius: 'var(--radius-sm)', padding: '1px 6px', whiteSpace: 'nowrap' }}>
      {s.label}
    </span>
  )
}

export function Segmented<T extends string>({ options, value, onChange, size = 'md' }: { options: { value: T; label: ReactNode; icon?: string }[]; value: T; onChange: (v: T) => void; size?: 'sm' | 'md' }) {
  const h = size === 'sm' ? 26 : 30
  return (
    <div style={{ display: 'inline-flex', border: '1px solid var(--border-strong)', borderRadius: 'var(--radius-0)', overflow: 'hidden', height: h }}>
      {options.map((o, i) => {
        const on = o.value === value
        return (
          <button
            key={o.value}
            /* Класс несёт hover и нажатие из ui.css; data-on снимает hover с
               включённого сегмента — он уже выбран. */
            className="seg-btn"
            data-on={on ? '' : undefined}
            onClick={() => onChange(o.value)}
            /* Фон, цвет и подчёркивание переходят вместе — порознь глаз ловит
               рассинхрон (принцип .tabs__trigger). transform в списке, иначе
               нажатие из .seg-btn срабатывало бы мгновенно. */
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, height: '100%', padding: '0 12px', border: 0, borderLeft: i ? '1px solid var(--border-default)' : 0, cursor: 'pointer', background: on ? 'var(--accent-soft)' : 'var(--surface-card)', color: on ? 'var(--accent-ink)' : 'var(--text-muted)', font: 'var(--font-ui)', fontSize: 'var(--text-sm)', fontWeight: on ? 600 : 400, whiteSpace: 'nowrap', boxShadow: on ? 'inset 0 -2px 0 var(--accent)' : 'none', transition: 'background var(--duration-fast) var(--ease-standard), color var(--duration-fast) var(--ease-standard), box-shadow var(--duration-fast) var(--ease-standard), transform var(--duration-press) var(--ease-out)' }}
          >
            {o.icon && <Icon name={o.icon} size={14} />}
            {o.label}
          </button>
        )
      })}
    </div>
  )
}

export function Field({ label, children, mono = false, span = false }: { label: string; children: ReactNode; mono?: boolean; span?: boolean }) {
  return (
    <div style={{ gridColumn: span ? '1 / -1' : undefined }}>
      <div style={{ font: 'var(--font-eyebrow)', textTransform: 'uppercase', letterSpacing: 'var(--tracking-caps)', color: 'var(--text-subtle)', marginBottom: 4 }}>{label}</div>
      <div style={{ font: mono ? 'var(--font-mono)' : 'var(--font-ui)', fontSize: mono ? 'var(--text-sm)' : 'var(--text-md)', color: 'var(--text-strong)' }}>{children}</div>
    </div>
  )
}

export function Toolbar({ children, style }: { children: ReactNode; style?: CSSProperties }) {
  return <div style={{ flex: '0 0 auto', display: 'flex', alignItems: 'center', gap: 8, padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)', background: 'var(--surface-card)', flexWrap: 'wrap', ...style }}>{children}</div>
}
export function ToolSpacer() {
  return <div style={{ flex: 1 }} />
}

/* ---- cumulative S-curve (plan / forecast / fact) ---- */
export function SCurve({
  periods,
  currentIndex,
  planEnd = 100,
  forecastEnd = 96,
  factEnd = 56,
  height = 230,
}: {
  periods: string[]
  currentIndex: number
  planEnd?: number
  forecastEnd?: number
  factEnd?: number
  height?: number
}) {
  const ref = useRef<HTMLDivElement>(null)
  const [w, setW] = useState(660)
  useEffect(() => {
    const el = ref.current
    if (!el || typeof ResizeObserver === 'undefined') return
    const ro = new ResizeObserver((es) => {
      for (const e of es) setW(e.contentRect.width)
    })
    ro.observe(el)
    return () => ro.disconnect()
  }, [])
  const n = periods.length
  const padL = 30
  const padR = 14
  const padT = 12
  const padB = 22
  const cw = Math.max(120, w - padL - padR)
  const ch = height - padT - padB
  const X = (i: number) => padL + (i / (n - 1)) * cw
  const Y = (v: number) => padT + (1 - v / 100) * ch
  const sm = (t: number) => t * t * (3 - 2 * t)
  const plan = periods.map((_, i) => planEnd * sm(i / (n - 1)))
  const fcst = periods.map((_, i) => forecastEnd * sm(i / (n - 1)))
  const fact = periods.map((_, i) => (i <= currentIndex ? factEnd * sm(currentIndex ? i / currentIndex : 0) : null))
  const lineOf = (arr: (number | null)[]) =>
    arr
      .map((v, i) => (v == null ? null : (i && arr[i - 1] != null ? 'L' : 'M') + X(i) + ' ' + Y(v)))
      .filter(Boolean)
      .join(' ')
  const factPts = fact.filter((v): v is number => v != null)
  const factArea = `M ${X(0)} ${Y(0)} ` + factPts.map((v, i) => 'L ' + X(i) + ' ' + Y(v)).join(' ') + ` L ${X(factPts.length - 1)} ${Y(0)} Z`
  return (
    <div ref={ref} style={{ width: '100%' }}>
      <svg width={w} height={height} style={{ display: 'block' }}>
        {[0, 25, 50, 75, 100].map((g) => (
          <g key={g}>
            <line x1={padL} y1={Y(g)} x2={padL + cw} y2={Y(g)} stroke="var(--border-subtle)" strokeWidth="1" />
            <text x={padL - 6} y={Y(g) + 3} textAnchor="end" fontSize="9" fill="var(--text-subtle)" fontFamily="var(--font-mono)">{g}</text>
          </g>
        ))}
        {periods.map((p, i) => (i % (n > 8 ? 2 : 1) === 0 ? <text key={p} x={X(i)} y={height - 6} textAnchor="middle" fontSize="9" fill="var(--text-subtle)" fontFamily="var(--font-mono)">{p}</text> : null))}
        <line x1={X(currentIndex)} y1={padT} x2={X(currentIndex)} y2={padT + ch} stroke="var(--accent)" strokeWidth="1" strokeDasharray="3 3" />
        <text x={X(currentIndex)} y={padT - 2} textAnchor="middle" fontSize="8.5" fill="var(--accent-ink)" fontFamily="var(--font-mono)">сегодня</text>
        <path d={factArea} fill="var(--green-500)" opacity="0.1" />
        <path d={lineOf(plan)} fill="none" stroke="var(--slate-400)" strokeWidth="1.6" strokeDasharray="5 4" />
        <path d={lineOf(fcst)} fill="none" stroke="var(--blue-500)" strokeWidth="1.8" />
        <path d={lineOf(fact)} fill="none" stroke="var(--green-500)" strokeWidth="2.4" />
        <circle cx={X(currentIndex)} cy={Y(factEnd)} r="3.5" fill="var(--green-500)" stroke="var(--surface-card)" strokeWidth="1.5" />
      </svg>
      <div style={{ display: 'flex', gap: 16, paddingLeft: padL, marginTop: 4 }}>
        <LegendDot color="--slate-400" dash label="План" />
        <LegendDot color="--blue-500" label="Прогноз" />
        <LegendDot color="--green-500" label="Факт" />
      </div>
    </div>
  )
}
function LegendDot({ color, dash, label }: { color: string; dash?: boolean; label: string }) {
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6, font: 'var(--font-ui)', fontSize: 'var(--text-sm)', color: 'var(--text-muted)' }}>
      <span style={{ width: 16, height: 0, borderTop: `2px ${dash ? 'dashed' : 'solid'} var(${color})` }} />
      {label}
    </span>
  )
}
